import { useEffect } from 'react';

import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import {
    Collapse,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { IconChevronDown, IconChevronUp } from '@tabler/icons';

// material-ui

// project imports
import { useLocation } from '@redwoodjs/router';

import useConfig from 'src/hooks/useConfig';

import { NavGroupProps } from '../NavGroup';
import NavItem from '../NavItem';

// assets

// ==============================|| SIDEBAR MENU LIST COLLAPSE ITEMS ||============================== //

interface NavCollapseProps {
    menu: NavGroupProps['item'];
    level: number;
    selectedId: string;
    setSelected: (menuId: any) => void;
}

const NavCollapse = ({ menu, level, selectedId, setSelected }: NavCollapseProps) => {
    const theme = useTheme();
    const { borderRadius } = useConfig();
    const selectedColor =
        selectedId === menu.id ? theme.palette.secondary.main : theme.palette.grey[200];

    const handleClick = () => {
        if (selectedId === menu.id) {
            setSelected(null);
        } else {
            setSelected(menu.id);
        }
    };

    const { pathname } = useLocation();
    const checkOpenForParent = (child: any, id?: string) => {
        child?.forEach((item: any) => {
            if (item?.url === pathname) {
                setSelected(id);
            }
        });
    };

    // menu collapse for sub-levels
    useEffect(() => {
        const childrens = menu.children ? menu.children : [];

        childrens.forEach((item: any) => {
            if (item.children?.length) {
                checkOpenForParent(item.children, menu.id);
            }
            if (pathname && pathname.includes('product-details')) {
                if (item.url && item.url.includes('product-details')) {
                    setSelected(menu.id);
                }
            }
            if (item.url === pathname) {
                setSelected(menu.id);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pathname, menu.children]);

    // menu collapse & item
    const menus = menu.children?.map((item) => {
        switch (item.type) {
            case 'collapse':
                return (
                    <NavCollapse
                        key={item.id}
                        menu={item}
                        level={level + 1}
                        selectedId={selectedId}
                        setSelected={setSelected}
                    />
                );
            case 'item':
            case 'modal':
                return <NavItem key={item.id} item={item} level={level + 1} />;
            default:
                return (
                    <Typography key={item.id} variant="h6" color="error" align="center">
                        Menu Items Error
                    </Typography>
                );
        }
    });

    const Icon = menu?.icon;
    const menuIcon = menu.icon ? (
        <Icon
            strokeWidth={1.5}
            size="1.3rem"
            style={{
                marginTop: 'auto',
                marginBottom: 'auto',
                color: selectedColor,
            }}
        />
    ) : (
        <FiberManualRecordIcon
            sx={{
                width: selectedId === menu.id ? 8 : 6,
                height: selectedId === menu.id ? 8 : 6,
                color: selectedColor,
            }}
            fontSize={level > 0 ? 'inherit' : 'medium'}
        />
    );

    return (
        <>
            <ListItemButton
                sx={{
                    borderRadius: `${borderRadius}px`,
                    mb: 0.5,
                    alignItems: 'flex-start',
                    backgroundColor: level > 1 ? 'transparent !important' : 'inherit',
                    py: level > 1 ? 1 : 1.25,
                    pl: `${level * 24}px`,
                    paddingLeft: 0,
                }}
                selected={selectedId === menu.id}
                data-testid={`${menu?.id}-nav-group`}
                onClick={handleClick}
            >
                <ListItemIcon
                    sx={{
                        my: 'auto',
                        minWidth: !menu.icon ? 18 : 36,
                        color: selectedColor,
                    }}
                >
                    {menuIcon}
                </ListItemIcon>
                <ListItemText
                    primary={
                        <Typography
                            variant={selectedId === menu.id ? 'h5' : 'body1'}
                            color={selectedColor}
                            sx={{ my: 'auto' }}
                        >
                            {menu.title}
                        </Typography>
                    }
                    secondary={
                        menu.caption && (
                            <Typography
                                variant="caption"
                                sx={{ ...theme.typography.subMenuCaption }}
                                display="block"
                                gutterBottom
                            >
                                {menu.caption}
                            </Typography>
                        )
                    }
                />
                {open && selectedId === menu.id ? (
                    <IconChevronUp
                        stroke={1.5}
                        size="1rem"
                        style={{ marginTop: 'auto', marginBottom: 'auto' }}
                        color={theme.palette.grey[200]}
                    />
                ) : (
                    <IconChevronDown
                        stroke={1.5}
                        size="1rem"
                        style={{ marginTop: 'auto', marginBottom: 'auto' }}
                        color={theme.palette.grey[200]}
                    />
                )}
            </ListItemButton>
            <Collapse in={selectedId === menu.id} timeout="auto" unmountOnExit>
                {selectedId === menu.id && (
                    <List
                        component="div"
                        disablePadding
                        sx={{
                            position: 'relative',
                            '&:after': {
                                content: "''",
                                position: 'absolute',
                                left: '20px',
                                top: 0,
                                height: '100%',
                                width: '1px',
                                background:
                                    theme.palette.mode === 'dark'
                                        ? theme.palette.dark.light
                                        : theme.palette.background.default,
                            },
                        }}
                    >
                        {menus}
                    </List>
                )}
            </Collapse>
        </>
    );
};

export default NavCollapse;
